import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
export function ThreeBackground() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({
    x: 0,
    y: 0
  });
  const animationFrameRef = useRef<number>();
  useEffect(() => {
    if (!containerRef.current) return;
    // Scene setup with optimizations
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 50;
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: false,
      powerPreference: 'high-performance' // Prefer performance over quality
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5)); // Cap pixel ratio for performance
    containerRef.current.appendChild(renderer.domElement);
    // Reduced rain particles for better performance
    const rainCount = 1500; // Reduced from 2500
    const rainGeometry = new THREE.BufferGeometry();
    const rainPositions = new Float32Array(rainCount * 3);
    const rainVelocities = new Float32Array(rainCount);
    const rainColors = new Float32Array(rainCount * 3);
    for (let i = 0; i < rainCount; i++) {
      rainPositions[i * 3] = (Math.random() - 0.5) * 200;
      rainPositions[i * 3 + 1] = Math.random() * 200 - 50;
      rainPositions[i * 3 + 2] = (Math.random() - 0.5) * 100;
      rainVelocities[i] = Math.random() * 0.6 + 0.4;
      // White/light blue rain
      const brightness = 0.7 + Math.random() * 0.3;
      rainColors[i * 3] = brightness;
      rainColors[i * 3 + 1] = brightness;
      rainColors[i * 3 + 2] = 1.0;
    }
    rainGeometry.setAttribute('position', new THREE.BufferAttribute(rainPositions, 3));
    rainGeometry.setAttribute('color', new THREE.BufferAttribute(rainColors, 3));
    const rainMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: {
          value: 0
        },
        mousePos: {
          value: new THREE.Vector2(0, 0)
        }
      },
      vertexShader: `
        attribute vec3 color;
        varying vec3 vColor;
        varying float vAlpha;
        uniform float time;
        uniform vec2 mousePos;
        
        void main() {
          vColor = color;
          vec3 pos = position;
          
          // Mouse interaction - slight drift away from cursor
          vec2 particlePos = pos.xy;
          float dist = distance(particlePos, mousePos);
          float influence = smoothstep(25.0, 0.0, dist);
          pos.x += influence * (pos.x - mousePos.x) * 0.2;
          
          // Fade based on distance
          vAlpha = smoothstep(0.0, 80.0, abs(pos.z));
          
          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          gl_PointSize = 2.5 * (100.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        varying float vAlpha;
        
        void main() {
          // Vertical streak shape
          vec2 coord = gl_PointCoord - vec2(0.5);
          float alpha = 1.0 - abs(coord.x) * 2.0;
          alpha *= vAlpha * 0.5;
          
          gl_FragColor = vec4(vColor, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      vertexColors: true
    });
    const rain = new THREE.Points(rainGeometry, rainMaterial);
    scene.add(rain);
    // Throttled mouse move handler
    let mouseTimeout: NodeJS.Timeout;
    const handleMouseMove = (event: MouseEvent) => {
      clearTimeout(mouseTimeout);
      mouseTimeout = setTimeout(() => {
        mouseRef.current.x = event.clientX / window.innerWidth * 2 - 1;
        mouseRef.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
      }, 16); // Throttle to ~60fps
    };
    window.addEventListener('mousemove', handleMouseMove, {
      passive: true
    });
    // Animation loop
    let time = 0;
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      time += 0.016;
      // Update rain
      const positions = rainGeometry.attributes.position.array as Float32Array;
      for (let i = 0; i < rainCount; i++) {
        positions[i * 3 + 1] -= rainVelocities[i];
        // Reset rain drops that fall below
        if (positions[i * 3 + 1] < -100) {
          positions[i * 3 + 1] = 100;
          positions[i * 3] = (Math.random() - 0.5) * 200;
          positions[i * 3 + 2] = (Math.random() - 0.5) * 100;
        }
      }
      rainGeometry.attributes.position.needsUpdate = true;
      // Update shader uniforms
      rainMaterial.uniforms.time.value = time;
      rainMaterial.uniforms.mousePos.value.set(mouseRef.current.x * 50, mouseRef.current.y * 50);
      renderer.render(scene, camera);
    };
    animate();
    // Handle resize with debounce
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      }, 100);
    };
    window.addEventListener('resize', handleResize, {
      passive: true
    });
    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      clearTimeout(mouseTimeout);
      clearTimeout(resizeTimeout);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      if (containerRef.current && renderer.domElement.parentNode) {
        containerRef.current.removeChild(renderer.domElement);
      }
      rainGeometry.dispose();
      rainMaterial.dispose();
      renderer.dispose();
    };
  }, []);
  return <div ref={containerRef} className="absolute inset-0 pointer-events-none z-0" style={{
    opacity: 0.7
  }} data-id="element-192" />;
}